import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUIStore } from "@/stores/useUIStore";
import { useAuthStore } from "@/stores/useAuthStore";
import CustomerHelper from "@/helpers/customerHelper";
import {
  extractApiErrorMessage,
  getApiErrorStatus,
} from "@/helpers/errorHandler";
import * as liveGroupsService from "@/services/liveGroupsService";
import type {
  Group,
  Session,
  Disease,
  ModalMode,
  ViewMode,
} from "@/types/domain";

export function useLiveGroups(
  props: {
    isLoggedIn?: boolean;
    initialDiseaseKey?: string;
    initialGroupSlug?: string;
    backUrl?: string;
    /** Internal flag: when true skips onMounted fetch (used by subcomponents that only need helpers) */
    _skipInit?: boolean;
  },
  emit: {
    (e: "group-created", group: Group): void;
    (e: "group-joined", group: Group): void;
    (e: "group-left", group: Group): void;
  },
) {
  const router = useRouter();
  const { t: $t } = useI18n();
  const uiStore = useUIStore();
  const authStore = useAuthStore();

  // Wrap vue-i18n with lg prefix to keep template calls unchanged
  const t = (key: string) =>
    $t("lg" + key.charAt(0).toUpperCase() + key.slice(1));

  // ─────────────────────────────────────────────
  // Auth
  // ─────────────────────────────────────────────
  function requireLogin(): boolean {
    if (CustomerHelper.isUserLogged()) return true;
    uiStore.openLogin({ allowClose: true, goToUrl: "" });
    return false;
  }

  // ─────────────────────────────────────────────
  // i18n — locale for disease list labels only (browser lang)
  // ─────────────────────────────────────────────
  const LOCALE = navigator.language?.split("-")[0] || "en";

  // ─────────────────────────────────────────────
  // Disease list
  // ─────────────────────────────────────────────
  const DISEASES_I18N = [
    {
      key: "diabetes",
      en: "Diabetes",
      es: "Diabetes",
      fr: "Diabète",
      de: "Diabetes",
    },
    {
      key: "hypertension",
      en: "Hypertension",
      es: "Hipertensión",
      fr: "Hypertension",
      de: "Bluthochdruck",
    },
    { key: "asthma", en: "Asthma", es: "Asma", fr: "Asthme", de: "Asthma" },
    {
      key: "multiple_sclerosis",
      en: "Multiple Sclerosis",
      es: "Esclerosis Múltiple",
      fr: "Sclérose en plaques",
      de: "Multiple Sklerose",
    },
    { key: "lupus", en: "Lupus", es: "Lupus", fr: "Lupus", de: "Lupus" },
    {
      key: "fibromyalgia",
      en: "Fibromyalgia",
      es: "Fibromialgia",
      fr: "Fibromyalgie",
      de: "Fibromyalgie",
    },
    {
      key: "rheumatoid_arthritis",
      en: "Rheumatoid Arthritis",
      es: "Artritis Reumatoide",
      fr: "Polyarthrite rhumatoïde",
      de: "Rheumatoide Arthritis",
    },
    {
      key: "endometriosis",
      en: "Endometriosis",
      es: "Endometriosis",
      fr: "Endométriose",
      de: "Endometriose",
    },
    {
      key: "pcos",
      en: "Polycystic Ovary Syndrome",
      es: "Síndrome de Ovario Poliquístico",
      fr: "Syndrome des ovaires polykystiques",
      de: "Polyzystisches Ovarsyndrom",
    },
    {
      key: "crohns",
      en: "Crohn's Disease",
      es: "Enfermedad de Crohn",
      fr: "Maladie de Crohn",
      de: "Morbus Crohn",
    },
    {
      key: "ibs",
      en: "Irritable Bowel Syndrome",
      es: "Síndrome del Intestino Irritable",
      fr: "Syndrome de l'intestin irritable",
      de: "Reizdarmsyndrom",
    },
    {
      key: "colitis",
      en: "Ulcerative Colitis",
      es: "Colitis Ulcerosa",
      fr: "Colite ulcéreuse",
      de: "Colitis ulcerosa",
    },
    {
      key: "psoriasis",
      en: "Psoriasis",
      es: "Psoriasis",
      fr: "Psoriasis",
      de: "Psoriasis",
    },
    {
      key: "celiac",
      en: "Celiac Disease",
      es: "Enfermedad Celíaca",
      fr: "Maladie cœliaque",
      de: "Zöliakie",
    },
    {
      key: "hypothyroidism",
      en: "Hypothyroidism",
      es: "Hipotiroidismo",
      fr: "Hypothyroïdie",
      de: "Hypothyreose",
    },
    {
      key: "depression",
      en: "Depression",
      es: "Depresión",
      fr: "Dépression",
      de: "Depression",
    },
    {
      key: "anxiety",
      en: "Anxiety Disorder",
      es: "Trastorno de Ansiedad",
      fr: "Trouble anxieux",
      de: "Angststörung",
    },
    {
      key: "bipolar",
      en: "Bipolar Disorder",
      es: "Trastorno Bipolar",
      fr: "Trouble bipolaire",
      de: "Bipolare Störung",
    },
    { key: "adhd", en: "ADHD", es: "TDAH", fr: "TDAH", de: "ADHS" },
    {
      key: "autism",
      en: "Autism Spectrum Disorder",
      es: "Trastorno del Espectro Autista",
      fr: "Trouble du spectre autistique",
      de: "Autismus-Spektrum-Störung",
    },
    {
      key: "parkinson",
      en: "Parkinson's Disease",
      es: "Enfermedad de Parkinson",
      fr: "Maladie de Parkinson",
      de: "Parkinson-Krankheit",
    },
    {
      key: "alzheimer",
      en: "Alzheimer's Disease",
      es: "Enfermedad de Alzheimer",
      fr: "Maladie d'Alzheimer",
      de: "Alzheimer-Krankheit",
    },
    {
      key: "epilepsy",
      en: "Epilepsy",
      es: "Epilepsia",
      fr: "Épilepsie",
      de: "Epilepsie",
    },
    {
      key: "migraine",
      en: "Migraine",
      es: "Migraña",
      fr: "Migraine",
      de: "Migräne",
    },
    {
      key: "chronic_fatigue",
      en: "Chronic Fatigue Syndrome",
      es: "Síndrome de Fatiga Crónica",
      fr: "Syndrome de fatigue chronique",
      de: "Chronisches Müdigkeitssyndrom",
    },
    {
      key: "lyme",
      en: "Lyme Disease",
      es: "Enfermedad de Lyme",
      fr: "Maladie de Lyme",
      de: "Lyme-Borreliose",
    },
    {
      key: "heart_disease",
      en: "Heart Disease",
      es: "Enfermedad Cardíaca",
      fr: "Maladie cardiaque",
      de: "Herzerkrankung",
    },
    { key: "copd", en: "COPD", es: "EPOC", fr: "BPCO", de: "COPD" },
    {
      key: "long_covid",
      en: "Long COVID",
      es: "COVID Persistente",
      fr: "COVID long",
      de: "Long COVID",
    },
    {
      key: "kidney_disease",
      en: "Kidney Disease",
      es: "Enfermedad Renal",
      fr: "Maladie rénale",
      de: "Nierenerkrankung",
    },
    { key: "cancer", en: "Cancer", es: "Cáncer", fr: "Cancer", de: "Krebs" },
    {
      key: "breast_cancer",
      en: "Breast Cancer",
      es: "Cáncer de mama",
      fr: "Cancer du sein",
      de: "Brustkrebs",
    },
    {
      key: "hiv",
      en: "HIV/AIDS",
      es: "VIH/SIDA",
      fr: "VIH/SIDA",
      de: "HIV/AIDS",
    },
    {
      key: "osteoporosis",
      en: "Osteoporosis",
      es: "Osteoporosis",
      fr: "Ostéoporose",
      de: "Osteoporose",
    },
    {
      key: "sleep_apnea",
      en: "Sleep Apnea",
      es: "Apnea del Sueño",
      fr: "Apnée du sommeil",
      de: "Schlafapnoe",
    },
    {
      key: "ibd",
      en: "Inflammatory Bowel Disease",
      es: "Enfermedad Inflamatoria Intestinal",
      fr: "Maladie inflammatoire de l'intestin",
      de: "Chronisch entzündliche Darmerkrankung",
    },
  ];

  const langKey: "en" | "es" | "fr" | "de" = (
    ["es", "fr", "de"] as const
  ).includes(LOCALE as any)
    ? (LOCALE as any)
    : "en";

  const DISEASES: Disease[] = DISEASES_I18N.map((d) => ({
    key: d.key,
    label: d[langKey] ?? d.en,
  })).sort((a, b) => a.label.localeCompare(b.label));

  // ─────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────
  const currentView = ref<ViewMode>("groups");
  const activeDetailGroup = ref<Group | null>(null);
  const sessions = ref<Session[]>([]);
  const loadingSessions = ref(false);

  const searchQuery = ref("");
  const showDiseaseDropdown = ref(false);
  const selectedDisease = ref<Disease | null>(null);
  const filteredDiseases = ref<Disease[]>(DISEASES.slice(0, 20));

  const groups = ref<Group[]>([]);
  const memberGroupIds = ref<Set<string>>(new Set());
  const joiningGroupId = ref<string | null>(null);
  const joinGroupError = ref<string | null>(null);

  const showModal = ref(false);
  const modalMode = ref<ModalMode>("create");
  const currentStep = ref(0);
  const STEP_SUCCESS = 99;
  const isSubmitting = ref(false);
  const submitError = ref("");
  const successTitle = ref("");
  const successMessage = ref("");
  const successMode = ref<"group" | "session">("group");
  const detailLinkCopied = ref(false);

  // ── Share popup ──────────────────────────────────────────────────────────────
  const sharePopupOpen = ref(false);
  const sharePopupUrl = ref("");
  const sharePopupWhatsAppUrl = ref("");
  const sharePopupTelegramUrl = ref("");
  const sharePopupCopied = ref(false);

  const modalDiseaseSearch = ref("");
  const modalSelectedDisease = ref<Disease | null>(null);
  const filteredModalDiseases = ref<Disease[]>(DISEASES);
  const modalDiseaseInputRef = ref<HTMLInputElement | null>(null);
  const isPrivate = ref(false);
  const createdGroupSlug = ref("");
  const createdGroup = ref<Group | null>(null);
  const createdSession = ref<Session | null>(null);
  const linkCopied = ref(false);

  const groupName = ref("");
  const groupDescription = ref("");

  const proposalDate = ref("");
  const proposalTime = ref("");
  const proposalDescription = ref("");
  const proposalMaxAttendees = ref<number | "">("");
  const expandedSessionId = ref<string | null>(null);

  // Leave confirm
  const showLeaveConfirm = ref(false);
  const isLeavingGroup = ref(false);
  const groupToLeave = ref<Group | null>(null);

  // Guest join popup state
  const guestJoinPopupSession = ref<Session | null>(null);
  const guestJoinPopupGroup = ref<Group | null>(null);
  const pendingJoinSession = ref<Session | null>(null);
  const guestJoinEmail = ref("");
  const guestJoinState = ref<"options" | "email" | "sent">("options");
  const guestJoinLoading = ref(false);
  const guestJoinError = ref("");

  // Session link copied
  const sessionLinkCopied = ref<string | null>(null);
  const groupCreatedCopied = ref(false);
  const sessionInfoCopied = ref(false);

  // ─────────────────────────────────────────────
  // Computed
  // ─────────────────────────────────────────────
  const toLocalISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;

  const todayISO = computed(() => toLocalISO(new Date()));

  const proposalDays = computed(() => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const iso = toLocalISO(d);
      const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
      const num = d.getDate();
      const month = d.toLocaleDateString(undefined, { month: "short" });
      const showMonth = d.getDate() === 1;
      const isToday = i === 0;
      days.push({ iso, weekday, num, month, showMonth, isToday });
    }
    return days;
  });

  const availableTimeSlots = computed(() => {
    const fmt = new Intl.DateTimeFormat(navigator.language, {
      hour: "numeric",
      minute: "2-digit",
    });
    const allSlots: { value: string; label: string }[] = [];
    for (let h = 8; h <= 22; h++) {
      const makeSlot = (hh: number, mm: number) => {
        const d = new Date();
        d.setHours(hh, mm, 0, 0);
        return {
          value: `${String(hh).padStart(2, "0")}:${String(mm).padStart(
            2,
            "0",
          )}`,
          label: fmt.format(d),
        };
      };
      allSlots.push(makeSlot(h, 0));
      if (h < 22) allSlots.push(makeSlot(h, 30));
    }
    if (proposalDate.value === todayISO.value) {
      const now = new Date();
      const cur = now.getHours() * 60 + now.getMinutes();
      return allSlots.filter((s) => {
        const [hh, mm] = s.value.split(":").map(Number);
        return hh * 60 + mm > cur;
      });
    }
    return allSlots;
  });

  const filteredGroups = computed(() => {
    const list = selectedDisease.value
      ? groups.value.filter((g) => g.diseaseKey === selectedDisease.value!.key)
      : groups.value;
    return [...list].sort((a, b) => {
      const aM = memberGroupIds.value.has(a.id) ? 0 : 1;
      const bM = memberGroupIds.value.has(b.id) ? 0 : 1;
      return aM - bM;
    });
  });

  // ─────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────
  function getDiseaseLabel(key: string) {
    return DISEASES.find((d) => d.key === key)?.label ?? key;
  }
  function isGroupMember(id: string) {
    return memberGroupIds.value.has(id);
  }
  function filterDiseases() {
    const q = searchQuery.value.toLowerCase();
    filteredDiseases.value = q
      ? DISEASES.filter((d) => d.label.toLowerCase().includes(q)).slice(0, 20)
      : DISEASES.slice(0, 20);
  }
  function filterModalDiseases() {
    const q = modalDiseaseSearch.value.toLowerCase();
    filteredModalDiseases.value = q
      ? DISEASES.filter((d) => d.label.toLowerCase().includes(q))
      : DISEASES;
  }
  function selectDisease(disease: Disease) {
    selectedDisease.value = disease;
    searchQuery.value = disease.label;
    showDiseaseDropdown.value = false;
  }
  function clearDisease() {
    selectedDisease.value = null;
    searchQuery.value = "";
    filteredDiseases.value = DISEASES.slice(0, 20);
  }
  function onSearchBlur() {
    setTimeout(() => {
      showDiseaseDropdown.value = false;
    }, 200);
  }
  function formatDay(dateStr: string): string {
    return new Date(dateStr).getDate().toString();
  }
  function formatMonthAbbr(dateStr: string): string {
    return new Intl.DateTimeFormat(navigator.language, {
      month: "short",
    }).format(new Date(dateStr));
  }
  function formatDayOfWeek(dateStr: string): string {
    return new Intl.DateTimeFormat(navigator.language, {
      weekday: "short",
    }).format(new Date(dateStr));
  }
  function formatWeekdayTime(dateStr: string): string {
    const d = new Date(dateStr);
    const weekday = new Intl.DateTimeFormat(navigator.language, {
      weekday: "long",
    }).format(d);
    const dayMonth = new Intl.DateTimeFormat(navigator.language, {
      day: "numeric",
      month: "long",
    }).format(d);
    const time = new Intl.DateTimeFormat(navigator.language, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
    return `${
      weekday.charAt(0).toUpperCase() + weekday.slice(1)
    }, ${dayMonth} · ${time}`;
  }

  // ─────────────────────────────────────────────
  // Share session
  // ─────────────────────────────────────────────
  function getSessionShareUrl(session: Session): string {
    return `${window.location.origin}/session/${
      session.accessSlug ?? session.id
    }`;
  }
  function getGroupShareUrl(): string {
    const g = activeDetailGroup.value;
    if (!g) return window.location.origin;
    if (!g.isPublic && g.privateSlug)
      return `${window.location.origin}/g/${g.privateSlug}`;
    return `${window.location.origin}/live-groups/${g.diseaseKey}`;
  }
  function buildSessionShareText(session: Session): string {
    const heart = String.fromCodePoint(0x1f499);
    const calendar = String.fromCodePoint(0x1f4c5);
    const url = getSessionShareUrl(session);
    const g = activeDetailGroup.value;
    const groupNameVal = g?.name ?? "";
    const diseaseLabel = g?.diseaseKey
      ? getDiseaseLabel(g.diseaseKey)
      : g?.name ?? "";
    const cta =
      g?.isPublic && diseaseLabel
        ? interpolate("shareSessionCtaPublic", { disease: diseaseLabel })
        : t("shareSessionCtaPrivate");
    const parts: string[] = [
      `${heart} ${groupNameVal}`,
      `${calendar} ${formatWeekdayTime(session.dateTime)}`,
      "",
    ];
    if (session.description) parts.push(session.description, "");
    parts.push(cta, "", url);
    return parts.join("\n");
  }
  function buildWhatsAppUrl(session: Session): string {
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(
      buildSessionShareText(session),
    )}`;
  }
  function buildTelegramUrl(session: Session): string {
    const url = getSessionShareUrl(session);
    return `https://t.me/share/url?url=${encodeURIComponent(
      url,
    )}&text=${encodeURIComponent(buildSessionShareText(session))}`;
  }
  async function copySessionLink(session: Session) {
    try {
      await navigator.clipboard.writeText(getSessionShareUrl(session));
      sessionLinkCopied.value = session.id;
      setTimeout(() => {
        sessionLinkCopied.value = null;
      }, 2000);
    } catch {
      /* clipboard not available */
    }
  }

  // Call is "active" from 30 min before start until 2 hours after (reasonable session length)
  function isCallActive(dateTimeStr: string): boolean {
    const start = new Date(dateTimeStr).getTime();
    const now = Date.now();
    return now >= start - 30 * 60 * 1000 && now <= start + 2 * 60 * 60 * 1000;
  }
  function isSessionPast(dateTimeStr: string): boolean {
    return Date.now() > new Date(dateTimeStr).getTime() + 2 * 60 * 60 * 1000;
  }
  function privateGroupUrl(slug: string): string {
    return `${window.location.origin}/g/${slug}`;
  }
  async function copyInviteLink(slug: string) {
    try {
      await navigator.clipboard.writeText(privateGroupUrl(slug));
      linkCopied.value = true;
      setTimeout(() => {
        linkCopied.value = false;
      }, 2000);
    } catch {
      /* clipboard not available */
    }
  }
  function detailGroupUrl(): string {
    const g = activeDetailGroup.value;
    if (!g) return window.location.href;
    return g.privateSlug
      ? privateGroupUrl(g.privateSlug)
      : window.location.href;
  }

  // ─── Share helpers ────────────────────────────────────────────────────────────
  function interpolate(key: string, vars: Record<string, string>): string {
    // Pass vars directly to $t so Vue i18n substitutes {placeholders} correctly.
    // (Calling t(key) first lets Vue i18n consume the placeholders with empty values.)
    return String($t("lg" + key.charAt(0).toUpperCase() + key.slice(1), vars));
  }
  function openWhatsApp(text: string) {
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
      "_blank",
    );
  }
  function openTelegram(text: string, url?: string) {
    const base = url
      ? `https://t.me/share/url?url=${encodeURIComponent(
          url,
        )}&text=${encodeURIComponent(text)}`
      : `https://t.me/share/url?url=${encodeURIComponent(
          window.location.href,
        )}&text=${encodeURIComponent(text)}`;
    window.open(base, "_blank");
  }

  // Detail group (with URL)
  function detailGroupShareText(): string {
    const g = activeDetailGroup.value;
    if (!g) return "";
    const heart = String.fromCodePoint(0x1f499); // 💙 — generated at runtime to avoid file encoding issues
    const url = detailGroupUrl();
    if (g.isPublic) {
      const disease = getDiseaseLabel(g.diseaseKey);
      return (
        heart +
        " " +
        interpolate("shareGroupCtaPublic", { group: g.name, disease, url })
      );
    }
    return (
      heart + " " + interpolate("shareGroupCtaPrivate", { group: g.name, url })
    );
  }
  function buildWhatsAppGroupUrl(): string {
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(
      detailGroupShareText(),
    )}`;
  }
  function buildTelegramGroupUrl(): string {
    const url = detailGroupUrl();
    const text = detailGroupShareText();
    return `https://t.me/share/url?url=${encodeURIComponent(
      url,
    )}&text=${encodeURIComponent(text)}`;
  }
  function shareDetailGroupWhatsApp() {
    openWhatsApp(detailGroupShareText());
  }
  function shareDetailGroupTelegram() {
    openTelegram(detailGroupShareText(), detailGroupUrl());
  }
  async function copyDetailGroupLink() {
    try {
      await navigator.clipboard.writeText(detailGroupUrl());
      detailLinkCopied.value = true;
      setTimeout(() => {
        detailLinkCopied.value = false;
      }, 2000);
    } catch {
      /* clipboard not available */
    }
  }

  // New group created popup
  function newGroupShareText(): string {
    const g = createdGroup.value;
    if (!g) return "";
    const heart = String.fromCodePoint(0x1f499);
    const url = g.privateSlug
      ? `${window.location.origin}/g/${g.privateSlug}`
      : `${window.location.origin}/live-groups/${g.diseaseKey}`;
    if (g.isPublic && g.diseaseKey) {
      const disease = getDiseaseLabel(g.diseaseKey);
      return heart + " " + interpolate("shareGroupText", { disease, url });
    }
    return (
      heart +
      " " +
      interpolate("shareGroupPrivateText", { name: g.name ?? "", url })
    );
  }
  function shareNewGroupWhatsApp() {
    openWhatsApp(newGroupShareText());
  }
  function shareNewGroupTelegram() {
    openTelegram(newGroupShareText());
  }
  async function copyNewGroupInvite() {
    try {
      await navigator.clipboard.writeText(newGroupShareText());
      groupCreatedCopied.value = true;
      setTimeout(() => {
        groupCreatedCopied.value = false;
      }, 2000);
    } catch {
      /* clipboard not available */
    }
  }

  // Session created popup
  function sessionShareText(): string {
    const s = createdSession.value;
    if (s) return buildSessionShareText(s);
    // fallback if session ref not set
    const g = activeDetailGroup.value;
    const url = window.location.href;
    return interpolate("shareSessionText", { group: g?.name ?? "", url });
  }
  function shareSessionWhatsApp() {
    openWhatsApp(sessionShareText());
  }
  function shareSessionTelegram() {
    openTelegram(sessionShareText());
  }
  async function copySessionInfo() {
    try {
      await navigator.clipboard.writeText(sessionShareText());
      sessionInfoCopied.value = true;
      setTimeout(() => {
        sessionInfoCopied.value = false;
      }, 2000);
    } catch {
      /* clipboard not available */
    }
  }

  // ─────────────────────────────────────────────
  // Share popup
  // ─────────────────────────────────────────────
  function openSharePopup(type: "group" | "session", session?: Session) {
    if (type === "group") {
      const url = getGroupShareUrl();
      const text = detailGroupShareText();
      sharePopupUrl.value = url;
      sharePopupWhatsAppUrl.value = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        text,
      )}`;
      sharePopupTelegramUrl.value = `https://t.me/share/url?url=${encodeURIComponent(
        url,
      )}&text=${encodeURIComponent(text)}`;
    } else if (session) {
      const url = getSessionShareUrl(session);
      const text = buildSessionShareText(session);
      sharePopupUrl.value = url;
      sharePopupWhatsAppUrl.value = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        text,
      )}`;
      sharePopupTelegramUrl.value = `https://t.me/share/url?url=${encodeURIComponent(
        url,
      )}&text=${encodeURIComponent(text)}`;
    }
    sharePopupCopied.value = false;
    sharePopupOpen.value = true;
  }
  async function sharePopupCopy() {
    try {
      await navigator.clipboard.writeText(sharePopupUrl.value);
      sharePopupCopied.value = true;
      setTimeout(() => {
        sharePopupCopied.value = false;
      }, 2000);
    } catch {
      /* clipboard not available */
    }
  }

  // ─────────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────────
  async function viewGroup(group: Group) {
    activeDetailGroup.value = group;
    sessions.value = [];
    currentView.value = "detail";
    loadingSessions.value = true;
    await fetchSessions(group.id);
    loadingSessions.value = false;
  }

  function selectProposalDay(iso: string) {
    if (proposalDate.value !== iso) proposalTime.value = "";
    proposalDate.value = iso;
  }

  // ─────────────────────────────────────────────
  // Modal
  // ─────────────────────────────────────────────
  function handleCreateGroup() {
    if (!requireLogin()) return;
    modalMode.value = "create";
    currentStep.value = 0;
    modalSelectedDisease.value = null;
    modalDiseaseSearch.value = "";
    filteredModalDiseases.value = DISEASES;
    isPrivate.value = false;
    createdGroupSlug.value = "";
    createdGroup.value = null;
    groupName.value = "";
    groupDescription.value = "";
    showModal.value = true;
  }
  function chooseGroupType(priv: boolean) {
    isPrivate.value = priv;
    currentStep.value = 1;
    if (!priv) nextTick(() => modalDiseaseInputRef.value?.focus());
  }
  function handleProposeSession() {
    if (!requireLogin()) return;
    modalMode.value = "propose-session";
    currentStep.value = 0;
    proposalDate.value = "";
    proposalTime.value = "";
    proposalDescription.value = "";
    proposalMaxAttendees.value = "";
    showModal.value = true;
  }
  function closeModal() {
    showModal.value = false;
    currentStep.value = 0;
  }
  function goToCreatedGroup() {
    if (!createdGroup.value) {
      closeModal();
      return;
    }
    closeModal();
    viewGroup(createdGroup.value);
  }

  // ─────────────────────────────────────────────
  // Join / Leave group
  // ─────────────────────────────────────────────
  async function handleJoinGroup(group: Group) {
    if (!CustomerHelper.isUserLogged()) {
      guestJoinPopupGroup.value = group;
      guestJoinEmail.value = "";
      guestJoinState.value = "options";
      guestJoinError.value = "";
      return;
    }
    if (joiningGroupId.value) return;
    joiningGroupId.value = group.id;
    joinGroupError.value = null;
    try {
      await liveGroupsService.joinGroup(group.id);
      memberGroupIds.value.add(group.id);
      const g = groups.value.find((g) => g.id === group.id);
      if (g) g.memberCount++;
      emit("group-joined", group);
    } catch (err) {
      if (getApiErrorStatus(err) === 409) {
        joinGroupError.value = t("lgAlreadyMember");
        memberGroupIds.value.add(group.id);
      } else {
        console.error("[CompanionGroups] join error", err);
        joinGroupError.value = t("genericError");
      }
    } finally {
      joiningGroupId.value = null;
    }
  }

  function handleLeaveGroup(group: Group) {
    groupToLeave.value = group;
    showLeaveConfirm.value = true;
  }
  async function confirmLeaveGroup() {
    if (!groupToLeave.value || isLeavingGroup.value) return;
    isLeavingGroup.value = true;
    const left = groupToLeave.value;
    try {
      await liveGroupsService.leaveGroup(left.id);
      emit("group-left", left);
      showLeaveConfirm.value = false;
      await fetchGroups();
    } catch (err) {
      console.error("[CompanionGroups] leave error", err);
    } finally {
      isLeavingGroup.value = false;
    }
  }

  // ─────────────────────────────────────────────
  // Submit: Create group
  // ─────────────────────────────────────────────
  async function handleSubmitCreate() {
    if (isSubmitting.value) return;
    if (!isPrivate.value && !modalSelectedDisease.value) return;
    if (isPrivate.value && !groupName.value.trim()) return;
    isSubmitting.value = true;
    submitError.value = "";
    try {
      const payload: liveGroupsService.CreateGroupPayload = isPrivate.value
        ? {
            isPublic: false,
            name: groupName.value.trim(),
            description: groupDescription.value.trim() || undefined,
            language: LOCALE,
          }
        : {
            isPublic: true,
            diseaseKey: modalSelectedDisease.value!.key,
            language: LOCALE,
          };
      const newGroup = await liveGroupsService.createGroup(payload);
      groups.value.unshift(newGroup);
      memberGroupIds.value.add(newGroup.id);
      createdGroupSlug.value = newGroup.privateSlug ?? "";
      createdGroup.value = newGroup;
      emit("group-created", newGroup);
      successTitle.value = t("createGroupSuccess");
      successMessage.value = isPrivate.value
        ? t("createGroupPrivateSuccessMsg")
        : t("createGroupSuccessMsg");
      successMode.value = "group";
      currentStep.value = STEP_SUCCESS;
    } catch (err) {
      console.error("[CompanionGroups] create error", err);
      submitError.value = extractApiErrorMessage(err, t("genericError"));
    } finally {
      isSubmitting.value = false;
    }
  }

  // ─────────────────────────────────────────────
  // Submit: Propose session
  // ─────────────────────────────────────────────
  async function handleSubmitSession() {
    if (!proposalDate.value || !proposalTime.value || isSubmitting.value)
      return;
    isSubmitting.value = true;
    submitError.value = "";
    const groupId = activeDetailGroup.value!.id;
    try {
      const newSession = await liveGroupsService.createSession(groupId, {
        dateTime: `${proposalDate.value}T${proposalTime.value}:00`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        description: proposalDescription.value || undefined,
        maxAttendees:
          proposalMaxAttendees.value !== ""
            ? Number(proposalMaxAttendees.value)
            : undefined,
      });
      sessions.value.unshift(newSession);
      createdSession.value = newSession;
      successTitle.value = t("proposeSessionSuccess");
      successMessage.value = t("proposeSessionSuccessMsg");
      successMode.value = "session";
      currentStep.value = STEP_SUCCESS;
    } catch (err) {
      console.error("[CompanionGroups] propose session error", err);
      submitError.value = extractApiErrorMessage(err, t("genericError"));
    } finally {
      isSubmitting.value = false;
    }
  }

  // ─────────────────────────────────────────────
  // Session attendance
  // ─────────────────────────────────────────────
  function openGuestJoinPopup(session: Session) {
    guestJoinPopupSession.value = session;
    guestJoinEmail.value = "";
    guestJoinState.value = "options";
    guestJoinError.value = "";
  }
  function closeGuestJoinPopup() {
    guestJoinPopupSession.value = null;
    guestJoinPopupGroup.value = null;
  }
  function guestJoinChooseLogin() {
    pendingJoinSession.value = guestJoinPopupSession.value;
    closeGuestJoinPopup();
    uiStore.openLogin({ allowClose: true, goToUrl: "" });
  }
  function guestJoinChooseRegister() {
    pendingJoinSession.value = guestJoinPopupSession.value;
    closeGuestJoinPopup();
    uiStore.openLogin({ allowClose: true, goToUrl: "" });
  }
  async function guestJoinSubmitEmail() {
    const email = guestJoinEmail.value.trim();
    if (!email) return;
    guestJoinLoading.value = true;
    guestJoinError.value = "";
    try {
      if (guestJoinPopupGroup.value) {
        await liveGroupsService.guestJoinGroup(
          guestJoinPopupGroup.value.id,
          email,
        );
      } else if (guestJoinPopupSession.value) {
        await liveGroupsService.guestJoinSession(
          guestJoinPopupSession.value.id,
          email,
        );
      } else {
        return;
      }
      guestJoinState.value = "sent";
    } catch (err) {
      if (getApiErrorStatus(err) === 409) {
        guestJoinError.value = t("sessionFull");
      } else {
        guestJoinError.value = t("genericError");
      }
    } finally {
      guestJoinLoading.value = false;
    }
  }

  // ─────────────────────────────────────────────
  // Join success popup
  // ─────────────────────────────────────────────
  const joinSuccessPopupOpen = ref(false);
  const joinSuccessSession = ref<Session | null>(null);

  function openJoinSuccess(session: Session) {
    joinSuccessSession.value = session;
    joinSuccessPopupOpen.value = true;
  }

  function closeJoinSuccess() {
    joinSuccessPopupOpen.value = false;
    joinSuccessSession.value = null;
  }

  function handleJoinSuccessShare() {
    const session = joinSuccessSession.value;
    closeJoinSuccess();
    if (session) openSharePopup("session", session);
  }

  async function toggleSessionAttendance(session: Session) {
    if (!CustomerHelper.isUserLogged()) {
      openGuestJoinPopup(session);
      return;
    }
    const groupId = activeDetailGroup.value!.id;
    try {
      if (session.isAttending) {
        const result = await liveGroupsService.leaveSession(groupId, session.id);
        if (result.deleted) {
          sessions.value = sessions.value.filter((s) => s.id !== session.id);
          expandedSessionId.value = null;
          activeDetailGroup.value = null;
          currentView.value = "groups";
          await router.push({ name: "Groups" });
          return;
        }
        session.isAttending = false;
        session.attendeeCount = Math.max(0, session.attendeeCount - 1);
        if (session.status === "confirmed") session.status = "open";
      } else {
        const updated = await liveGroupsService.joinSession(
          groupId,
          session.id,
        );
        session.isAttending = true;
        session.attendeeCount++;
        session.spotsLeft = updated.spotsLeft;
        if (updated.status === "confirmed") {
          session.status = "confirmed";
          session.jitsiLink = updated.jitsiLink;
        }
        openJoinSuccess(session);
      }
    } catch (err) {
      console.error("[CompanionGroups] attendance error", err);
      if (getApiErrorStatus(err) === 409) {
        session.spotsLeft = 0;
        alert(t("sessionFull"));
      }
    }
  }

  async function fetchGroups() {
    try {
      const data = await liveGroupsService.fetchGroups(LOCALE);
      groups.value = data.groups;
      memberGroupIds.value = new Set(data.memberGroupIds);
    } catch (err) {
      console.error("[LiveGroups] fetchGroups error", err);
      groups.value = [];
    }
  }

  async function fetchSessions(groupId: string) {
    try {
      sessions.value = await liveGroupsService.fetchSessions(groupId);
    } catch (err) {
      console.error("[LiveGroups] fetchSessions error", err);
      sessions.value = [];
    }
  }

  async function onLoginSuccess() {
    if (!pendingJoinSession.value) return;
    const sessionId = pendingJoinSession.value.id;
    const group = activeDetailGroup.value;
    pendingJoinSession.value = null;

    if (!group) return;

    try {
      await liveGroupsService.joinSession(group.id, sessionId);
    } catch (err) {
      // 409 = already attending or full — still refresh below
      if (getApiErrorStatus(err) !== 409) {
        console.error("[LiveGroups] auto-join after login failed", err);
        return;
      }
    }

    // Re-fetch groups (to update memberGroupIds) and sessions
    await Promise.all([fetchGroups(), fetchSessions(group.id)]);
    // Show success popup — find the refreshed session object by ID
    const joined = sessions.value.find((s) => s.id === sessionId);
    if (joined) openJoinSuccess(joined);
  }

  // Watch auth store: when user goes from null → logged in, auto-join pending session
  if (!props._skipInit) {
    watch(
      () => authStore.user,
      async (newUser, oldUser) => {
        if (newUser && !oldUser && pendingJoinSession.value) {
          await onLoginSuccess();
        }
      },
    );
  }

  onMounted(async () => {
    if (props._skipInit) return;

    await fetchGroups();

    if (props.initialDiseaseKey) {
      const group = groups.value.find(
        (g) => g.diseaseKey === props.initialDiseaseKey,
      );
      if (group) {
        viewGroup(group);
        return;
      }
    }

    if (props.initialGroupSlug) {
      const group = groups.value.find(
        (g) => g.privateSlug === props.initialGroupSlug,
      );
      if (group) {
        viewGroup(group);
        return;
      }

      try {
        const g = await liveGroupsService.getGroupBySlug(
          props.initialGroupSlug,
        );
        groups.value.unshift(g);
        viewGroup(g);
      } catch {
        // slug not found — stay on list
      }
    }
  });

  return {
    // i18n
    t,
    // Constants
    LOCALE,
    STEP_SUCCESS,
    // State — views
    currentView,
    groups,
    memberGroupIds,
    joiningGroupId,
    joinGroupError,
    // State — detail
    activeDetailGroup,
    sessions,
    loadingSessions,
    expandedSessionId,
    // State — search
    searchQuery,
    showDiseaseDropdown,
    selectedDisease,
    filteredDiseases,
    // State — modal
    showModal,
    modalMode,
    currentStep,
    isSubmitting,
    submitError,
    successTitle,
    successMessage,
    successMode,
    isPrivate,
    modalDiseaseSearch,
    modalSelectedDisease,
    filteredModalDiseases,
    modalDiseaseInputRef,
    groupName,
    groupDescription,
    createdGroup,
    createdGroupSlug,
    groupCreatedCopied,
    linkCopied,
    sessionInfoCopied,
    detailLinkCopied,
    // State — session proposal
    proposalDate,
    proposalTime,
    proposalDescription,
    proposalMaxAttendees,
    // State — share popup
    sharePopupOpen,
    sharePopupUrl,
    sharePopupWhatsAppUrl,
    sharePopupTelegramUrl,
    sharePopupCopied,
    // State — leave confirm
    showLeaveConfirm,
    groupToLeave,
    isLeavingGroup,
    // State — guest join
    guestJoinPopupSession,
    guestJoinPopupGroup,
    guestJoinEmail,
    guestJoinState,
    guestJoinLoading,
    guestJoinError,
    // State — session link
    sessionLinkCopied,
    // Computed
    filteredGroups,
    proposalDays,
    availableTimeSlots,
    todayISO,
    // Helpers
    getDiseaseLabel,
    isGroupMember,
    filterDiseases,
    filterModalDiseases,
    selectDisease,
    clearDisease,
    onSearchBlur,
    formatDay,
    formatMonthAbbr,
    formatDayOfWeek,
    formatWeekdayTime,
    isCallActive,
    isSessionPast,
    privateGroupUrl,
    copyInviteLink,
    detailGroupUrl,
    interpolate,
    getSessionShareUrl,
    buildSessionShareText,
    buildWhatsAppUrl,
    buildTelegramUrl,
    copySessionLink,
    detailGroupShareText,
    buildWhatsAppGroupUrl,
    buildTelegramGroupUrl,
    shareDetailGroupWhatsApp,
    shareDetailGroupTelegram,
    copyDetailGroupLink,
    newGroupShareText,
    shareNewGroupWhatsApp,
    shareNewGroupTelegram,
    copyNewGroupInvite,
    sessionShareText,
    shareSessionWhatsApp,
    shareSessionTelegram,
    copySessionInfo,
    // Share popup
    openSharePopup,
    sharePopupCopy,
    // Navigation
    viewGroup,
    selectProposalDay,
    // Modal
    handleCreateGroup,
    chooseGroupType,
    handleProposeSession,
    closeModal,
    goToCreatedGroup,
    // Join / Leave group
    handleJoinGroup,
    handleLeaveGroup,
    confirmLeaveGroup,
    // Submit
    handleSubmitCreate,
    handleSubmitSession,
    // Join success popup
    joinSuccessPopupOpen,
    joinSuccessSession,
    closeJoinSuccess,
    handleJoinSuccessShare,
    // Session attendance
    toggleSessionAttendance,
    openGuestJoinPopup,
    closeGuestJoinPopup,
    guestJoinChooseLogin,
    guestJoinChooseRegister,
    guestJoinSubmitEmail,
    // Router (needed by GroupDetail back button)
    router,
  };
}
